import React, { useState } from 'react';
import { T } from '../translations';

// ─── Design tokens (match App.js) ───────────────────────────────────────────
const C = {
  navy:       '#1B2437',
  navyMid:    '#223049',
  orange:     '#F59420',
  white:      '#ffffff',
  off:        '#f8f9fa',
  border:     '#e5e7eb',
  textDark:   '#111827',
  text:       '#374151',
  muted:      '#6b7280',
  success:    '#22c55e',
  r:          '8px',
};

const IconCheck = ({ size = 16, color = C.success }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 8l4 4 8-8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconX = ({ size = 16, color = C.muted }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3l10 10M13 3L3 13" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconFlagNL = () => (
  <svg width="32" height="22" viewBox="0 0 32 22" aria-label="NL" role="img">
    <rect width="32" height="7.3" fill="#AE1C28" />
    <rect y="7.3" width="32" height="7.3" fill="#fff" />
    <rect y="14.7" width="32" height="7.3" fill="#21468B" />
  </svg>
);

export default function GlobalCarePage({ lang = 'es' }) {
  const [formState, setFormState] = useState({ sending: false, sent: false, error: false });
  const t = T[lang];

  async function handleSubmit(e) {
    e.preventDefault();
    setFormState({ sending: true, sent: false, error: false });
    const form = e.target;
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries());
    try {
      const res = await fetch('https://formspree.io/f/mnjgjqby', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setFormState({ sending: false, sent: true, error: false });
        form.reset();
      } else {
        setFormState({ sending: false, sent: false, error: true });
      }
    } catch {
      setFormState({ sending: false, sent: false, error: true });
    }
  }

  const careSteps = [
    { h: t.step1_h, p: t.step1_p },
    { h: t.step2_h, p: t.step2_p },
    { h: t.step3_h, p: t.care_step3_p },
    { h: t.step4_h, p: t.step4_p },
  ];

  const careReqs = [
    { icon: <IconCheck size={22} color={C.orange} />, label: t.care_req_profile_label, val: t.care_req_profile_val },
    { icon: <IconFlagNL />, label: t.care_req_dutch_label, val: t.care_req_dutch_val },
    { icon: <IconCheck size={22} color={C.orange} />, label: t.care_req_passport_label, val: t.care_req_passport_val },
    { icon: <IconCheck size={22} color={C.orange} />, label: t.care_req_availability_label, val: t.care_req_availability_val },
  ];

  const btnOrange = {
    background: C.orange, color: C.white, border: 'none',
    padding: '13px 28px', borderRadius: C.r,
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    textDecoration: 'none', transition: 'background 0.2s',
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif", color: C.text, background: C.white }}>
      {/* ════════════════════════════════════════════════════ HERO */}
      <section id="top" style={{
        position: 'relative', minHeight: '70vh',
        overflow: 'hidden', background: C.navy,
        display: 'flex', alignItems: 'center', padding: '80px 48px',
      }}>
        <div style={{ maxWidth: 680, position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'inline-block', background: C.orange, color: C.white,
            fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
            padding: '4px 12px', borderRadius: 12, marginBottom: 20,
          }}>
            LPC Global Care
          </div>
          <h1 style={{
            fontSize: 44, fontWeight: 900, lineHeight: 1.15,
            letterSpacing: -1.5, marginBottom: 20, color: C.white,
          }}>
            {t.care_hero_h1}
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: 32 }}>
            {t.care_hero_sub}
          </p>
          <a href="#contact" style={{ ...btnOrange, fontSize: 16, padding: '14px 32px' }}>
            {t.care_cta} →
          </a>
        </div>
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: '45%',
          background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img
            src=""
            alt="Colombian nurse or healthcare professional working in a Dutch hospital or care facility"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ PROGRAMME DETAILS */}
      <section style={{ padding: '88px 48px', background: C.off }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.orange, fontWeight: 700, marginBottom: 10 }}>
            {t.care_prog_eyebrow}
          </div>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: C.textDark, letterSpacing: -1.2, lineHeight: 1.15, marginBottom: 32 }}>
            {t.care_h3}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' }}>
            <div>
              <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 24, height: 240, background: 'rgba(27,36,55,0.06)' }}>
                <img
                  src=""
                  alt="Healthcare professional in Dutch elderly care or hospital setting"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
              <p style={{ fontSize: 16, color: C.text, lineHeight: 1.75 }}>
                {t.care_p}
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.textDark, marginBottom: 16 }}>
                {t.care_what_title}
              </h3>
              <ul style={{ paddingLeft: 20, marginBottom: 24, color: C.text, fontSize: 15, lineHeight: 1.8 }}>
                <li key="lang" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, listStyle: 'none', marginLeft: -20 }}>
                  <span style={{ flexShrink: 0, marginTop: 2 }}><IconX size={14} /></span>
                  <span>
                    <span style={{ color: C.muted, fontWeight: 600, fontSize: 13 }}>{t.care_what_lang_not_included_label}</span>
                    {' — '}{t.care_what_lang_desc}
                  </span>
                </li>
                {(t.care_what_list || []).map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, listStyle: 'none', marginLeft: -20 }}>
                    <span style={{ flexShrink: 0, marginTop: 2, color: C.success }}>✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div style={{
                background: 'rgba(245,148,32,0.06)', borderLeft: `4px solid ${C.orange}`,
                padding: '16px 20px', borderRadius: '0 8px 8px 0',
                fontSize: 14, color: C.textDark, lineHeight: 1.6,
              }}>
                <strong>{t.req_label}</strong> {t.care_req}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ REQUIREMENTS */}
      <section style={{ padding: '56px 48px', background: C.navy }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.orange, fontWeight: 700, marginBottom: 10 }}>
            {t.care_reqs_eyebrow}
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: C.white, letterSpacing: -1.2, lineHeight: 1.15, marginBottom: 36 }}>
            {t.care_reqs_h2}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {careReqs.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 16,
                background: 'rgba(255,255,255,0.04)', padding: '24px 20px',
                borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)',
              }}>
                {item.icon}
                <div>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.white }}>{item.val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ HOW IT WORKS */}
      <section id="process" style={{ padding: '88px 48px', background: C.off }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.orange, fontWeight: 700, marginBottom: 10 }}>
            {t.proc_eyebrow}
          </div>
          <h2 style={{ fontSize: 40, fontWeight: 800, color: C.textDark, letterSpacing: -1.2, lineHeight: 1.15, marginBottom: 56 }}>
            {t.proc_h2}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32 }}>
            {careSteps.map((step, n) => (
              <div key={n} style={{ position: 'relative' }}>
                {n < 3 && (
                  <div style={{
                    position: 'absolute', top: 22, left: 44, right: -20,
                    height: 2, background: `linear-gradient(to right, ${C.orange}, rgba(245,148,32,0.2))`,
                    zIndex: 0,
                  }} />
                )}
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: C.orange, color: C.white, fontSize: 18, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20, position: 'relative', zIndex: 1,
                  boxShadow: '0 4px 12px rgba(245,148,32,0.35)',
                }}>
                  {n + 1}
                </div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: C.textDark, marginBottom: 8 }}>
                  {step.h}
                </h4>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.65 }}>
                  {step.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ CONTACT FORM */}
      <section id="contact" style={{ padding: '88px 48px', background: C.navy }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.orange, fontWeight: 700, marginBottom: 10 }}>
            {t.form_eyebrow}
          </div>
          <h2 style={{
            fontSize: 40, fontWeight: 800, color: C.white,
            letterSpacing: -1.2, lineHeight: 1.15, marginBottom: 12,
          }}>
            {t.form_h2}
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', marginBottom: 44 }}>{t.form_sub}</p>

          {formState.sent ? (
            <div style={{
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: C.r, padding: '24px 28px', color: C.success, fontSize: 16, lineHeight: 1.6,
            }}>{t.form_success}</div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <input type="hidden" name="_language" value={lang} />
              <input type="hidden" name="program" value="globalcare" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>{t.form_name} *</label>
                  <input name="name" type="text" required placeholder={t.form_name_ph} className="form-input"
                    style={{ width:'100%', padding:'12px 16px', borderRadius: C.r, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.06)', color: C.white, fontSize: 15 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>{t.form_email} *</label>
                  <input name="email" type="email" required placeholder={t.form_email_ph} className="form-input"
                    style={{ width:'100%', padding:'12px 16px', borderRadius: C.r, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.06)', color: C.white, fontSize: 15 }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>{t.form_phone}</label>
                <input name="phone" type="tel" placeholder={t.form_phone_ph} className="form-input"
                  style={{ width:'100%', padding:'12px 16px', borderRadius: C.r, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.06)', color: C.white, fontSize: 15 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>{t.form_message}</label>
                <textarea name="message" rows={4} placeholder={t.form_message_ph} className="form-input"
                  style={{ width:'100%', padding:'12px 16px', borderRadius: C.r, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.06)', color: C.white, fontSize: 15, resize:'vertical', minHeight: 110 }} />
              </div>
              {formState.error && (
                <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius: C.r, padding:'12px 16px' }}>
                  <p style={{ color:'#ef4444', fontSize: 14 }}>{t.form_error}</p>
                  <a href="https://wa.me/573059469665" style={{ color: C.orange, fontSize: 14, fontWeight: 700 }}>WhatsApp →</a>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
                <input type="checkbox" required name="privacy_accept" style={{ marginTop: 3, flexShrink: 0 }} />
                <span>
                  <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer" style={{ color: C.orange, textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>
                    {t.privacy_checkbox}
                  </a>
                </span>
              </label>
              <button type="submit" disabled={formState.sending} style={{
                background: formState.sending ? C.muted : C.orange,
                color: C.white, padding:'16px 32px', borderRadius: C.r,
                fontWeight: 700, fontSize: 16, border: 'none',
                cursor: formState.sending ? 'not-allowed' : 'pointer', transition:'background 0.2s', marginTop: 4,
              }}>
                {formState.sending ? t.form_sending : t.form_submit}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
